import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  ExternalLink,
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { api } from '../../api';
import { PostSummary } from '../../types';
import { buildFolderTree, countItems, FolderNode } from '../../lib/tree';

export function AdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: posts, isLoading } = useQuery({
    queryKey: ['admin-posts'],
    queryFn: () => api.adminListPosts(),
  });

  const tree = useMemo(() => buildFolderTree(posts ?? [], (p) => p.folder), [posts]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deletePost(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-posts'] }),
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mx-auto max-w-4xl px-4 pb-20 pt-24 sm:px-6 lg:px-8"
    >
      <header className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-star">文章管理</h1>
          <p className="text-slate-600 dark:text-star-dim">创建、编辑和发布你的文章</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/hero')}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:border-indigo-500 hover:text-indigo-600 dark:border-indigo-500/20 dark:text-star-dim dark:hover:text-nebula-glow"
          >
            首页标题
          </button>
          <button
            onClick={() => navigate('/admin/profile')}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:border-indigo-500 hover:text-indigo-600 dark:border-indigo-500/20 dark:text-star-dim dark:hover:text-nebula-glow"
          >
            个人主页
          </button>
          <button
            onClick={() => navigate('/admin/new')}
            className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition-transform hover:scale-105 dark:bg-nebula"
          >
            <Plus size={16} /> 新建文章
          </button>
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-200 dark:bg-space-800"></div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 dark:border-indigo-500/20 dark:bg-space-800/60">
          {tree.children.map((folder) => (
            <FolderRow
              key={folder.path}
              node={folder}
              depth={0}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
          {tree.items.map((post) => (
            <PostRow key={post.id} post={post} depth={0} onDelete={() => deleteMutation.mutate(post.id)} />
          ))}
          {posts?.length === 0 && (
            <div className="py-20 text-center text-slate-500 dark:text-star-dim">
              还没有文章，点击上方按钮创建第一篇。
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

function FolderRow({
  node,
  depth,
  onDelete,
}: {
  node: FolderNode<PostSummary>;
  depth: number;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-space-800/80"
        style={{ paddingLeft: `${depth * 1.5 + 0.75}rem` }}
      >
        {open ? (
          <ChevronDown size={16} className="shrink-0 text-slate-400" />
        ) : (
          <ChevronRight size={16} className="shrink-0 text-slate-400" />
        )}
        {open ? (
          <FolderOpen size={16} className="shrink-0 text-indigo-500 dark:text-nebula-glow" />
        ) : (
          <Folder size={16} className="shrink-0 text-indigo-500 dark:text-nebula-glow" />
        )}
        <span className="font-medium text-slate-800 dark:text-star">{node.name}</span>
        <span className="text-xs text-slate-400 dark:text-star-dim">{countItems(node)} 篇</span>
      </button>
      {open && (
        <div>
          {node.children.map((child) => (
            <FolderRow key={child.path} node={child} depth={depth + 1} onDelete={onDelete} />
          ))}
          {node.items.map((post) => (
            <PostRow key={post.id} post={post} depth={depth + 1} onDelete={() => onDelete(post.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PostRow({
  post,
  depth,
  onDelete,
}: {
  post: PostSummary;
  depth: number;
  onDelete: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div
      className="group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-space-800/80"
      style={{ paddingLeft: `${depth * 1.5 + 2.75}rem` }}
    >
      <FileText size={16} className="shrink-0 text-slate-400" />
      <span className="min-w-0 flex-1 truncate font-medium text-slate-900 dark:text-star">
        {post.title}
      </span>
      <StatusBadge status={post.status} />
      <span className="hidden w-24 shrink-0 text-right text-xs text-slate-400 dark:text-star-dim sm:block">
        {post.published_at ? dayjs(post.published_at).format('YYYY-MM-DD') : '-'}
      </span>
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {post.status === 'public' && (
          <Link
            to={`/posts/${post.slug}`}
            target="_blank"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-space-700 dark:hover:text-star"
          >
            <ExternalLink size={15} />
          </Link>
        )}
        <button
          onClick={() => navigate(`/admin/edit/${post.id}`)}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-500/10 dark:hover:text-nebula-glow"
        >
          <Edit2 size={15} />
        </button>
        <button
          onClick={onDelete}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: React.ReactNode; className: string; label: string }> = {
    public: {
      icon: <Eye size={12} />,
      label: '已发布',
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
    },
    private: {
      icon: <EyeOff size={12} />,
      label: '私有',
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
    },
    draft: {
      icon: <FileText size={12} />,
      label: '草稿',
      className: 'bg-slate-100 text-slate-700 dark:bg-space-700 dark:text-slate-300',
    },
  };

  const c = config[status] || config.draft;

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${c.className}`}
    >
      {c.icon}
      {c.label}
    </span>
  );
}
